// SystemOverview.js
// System Overview panel showing architecture overview, metadata, flows, and CTAs

import React, { useState } from "react";
import { 
  BookOpen, 
  GitBranch, 
  Database, 
  Code, 
  MessageSquare, 
  FileText,
  PlayCircle,
  RefreshCw,
  Package
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "./api";
import AnalysisCoverageBlock from "./AnalysisCoverageBlock";
import CodebaseGuidePanel from "./CodebaseGuidePanel";
import "./SystemOverview.css";

/** Matches server placeholders from generateArchitectureOverview failures (openai.ts). */
function isArchitectureOverviewFailureText(text) {
  if (!text || typeof text !== "string") return false;
  const t = text.trim();
  return (
    t.includes("Unable to generate due to an error") ||
    (t.startsWith("Architecture overview for ") && t.includes(" - Generation failed"))
  );
}

const SystemOverview = ({ 
  repo, 
  onOpenChat, 
  onOpenFiles,
  onRepoUpdate,
  metadataLoading = false,
  isAdmin = false,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  if (!repo) return null;

  const { architectureOverview, metadata, flows } = repo;
  const overviewShowsFailure = isArchitectureOverviewFailureText(architectureOverview);
  /** Starter overviews are shared globally; only admins should trigger regeneration (server enforces too). */
  const allowGenerateArchitecture =
    !repo.isStarter || isAdmin;

  // Extract key files from entrypoints and flows
  const getKeyFiles = () => {
    const files = new Set();
    if (metadata?.entrypoints) {
      metadata.entrypoints.forEach(ep => {
        if (ep.file) files.add(ep.file);
      });
    }
    if (flows && flows.length > 0) {
      flows.slice(0, 5).forEach(flow => {
        flow.steps?.slice(0, 3).forEach(step => {
          if (step.filePath) files.add(step.filePath);
        });
      });
    }
    return Array.from(files).slice(0, 10);
  };

  const keyFiles = getKeyFiles();

  const handleGenerateOverview = async () => {
    if (!repo._id || isGenerating) return;

    setIsGenerating(true);
    setError(null);

    try {
      const response = await api.post(`/repos/${repo._id}/architecture-overview/generate`);
      
      // Update the repo with the new overview
      if (onRepoUpdate && response.data.overview) {
        onRepoUpdate({
          ...repo,
          architectureOverview: response.data.overview
        });
      }
    } catch (err) {
      console.error("Failed to generate architecture overview:", err);
      const data = err.response?.data;
      const msg = data?.detail || data?.error || "Failed to generate architecture overview. Please try again.";
      setError(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="system-overview">
      <div className="overview-main">
        <div className="overview-header">
          <BookOpen size={24} />
          <h2>Codebase Guide</h2>
        </div>

        <CodebaseGuidePanel repoId={repo._id} />

        {architectureOverview && !overviewShowsFailure ? (
          <div className="architecture-content">
            <div className="architecture-section-label">Architecture overview</div>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {architectureOverview}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="architecture-placeholder">
            <p>Architecture overview provides a comprehensive explanation of the system's structure and design.</p>
            {overviewShowsFailure && (
              <div className="architecture-error">
                <p>
                  The last generation did not complete. Check that OpenAI is configured and try again.
                </p>
              </div>
            )}
            {repo.isStarter && !isAdmin && (overviewShowsFailure || !architectureOverview) && (
              <p className="hint">
                This starter’s overview is shared with everyone. Only an administrator can generate or refresh it.
              </p>
            )}
            {error && (
              <div className="architecture-error">
                <p>{error}</p>
              </div>
            )}
            {allowGenerateArchitecture && (
              <button
                className="generate-overview-btn"
                onClick={handleGenerateOverview}
                disabled={isGenerating || repo.status !== 'completed'}
              >
                {isGenerating ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    {overviewShowsFailure ? "Retry architecture overview" : "Generate Architecture Overview"}
                  </>
                )}
              </button>
            )}
            {repo.status !== 'completed' && (
              <p className="hint">Please wait for repository processing to complete.</p>
            )}
          </div>
        )}
      </div>

      <div className="overview-sidebar">
        {metadataLoading && (
          <div className="sidebar-block">
            <div className="block-content">
              <p className="loading-text">Loading metadata...</p>
            </div>
          </div>
        )}

        {/* Public API (deterministic surface) */}
        {!metadataLoading && (
          <div className="sidebar-block public-api-block">
            <div className="block-header">
              <Package size={18} />
              <h3>Public API</h3>
            </div>
            <div className="block-content">
              {repo.publicSurface && repo.publicSurface.length > 0 ? (
                <>
                  <div className="public-api-summary">{repo.publicSurface.length} export{repo.publicSurface.length !== 1 ? 's' : ''} detected</div>
                  <ul className="public-api-list">
                    {(repo.publicSurface.slice(0, 20)).map((item, idx) => (
                      <li key={idx} className="public-api-item">
                        <span className="public-api-symbol">{item.symbolName}</span>
                        <span className={`public-api-confidence confidence-${item.confidence.toLowerCase()}`}>{item.confidence}</span>
                        <span className="public-api-file" title={item.filePath}>{(item.filePath || '').split('/').pop() || '—'}</span>
                      </li>
                    ))}
                  </ul>
                  {repo.publicSurface.length > 20 && (
                    <p className="public-api-more">+{repo.publicSurface.length - 20} more</p>
                  )}
                </>
              ) : (
                <p className="public-api-empty">No deterministic public API detected.</p>
              )}
            </div>
          </div>
        )}

        {/* Analysis Coverage (reusable block) */}
        {repo && (
          <div className="sidebar-block">
            <AnalysisCoverageBlock repo={repo} title="Repository readiness" />
          </div>
        )}
        
        {/* Framework & Stack */}
        {!metadataLoading && metadata?.framework && (
          <div className="sidebar-block">
            <div className="block-header">
              <Code size={18} />
              <h3>Framework & Stack</h3>
            </div>
            <div className="block-content">
              <div className="framework-info">
                <strong>{metadata.framework.name}</strong>
                {metadata.framework.version && (
                  <span className="version">v{metadata.framework.version}</span>
                )}
              </div>
              {metadata.build?.tools && metadata.build.tools.length > 0 && (
                <div className="build-tools">
                  <span className="label">Build:</span>
                  <span>{metadata.build.tools.join(", ")}</span>
                </div>
              )}
              {metadata.tests?.frameworks && metadata.tests.frameworks.length > 0 && (
                <div className="test-frameworks">
                  <span className="label">Tests:</span>
                  <span>{metadata.tests.frameworks.join(", ")}</span>
                  {metadata.tests.hasCoverageArtifacts && (
                    <span className="coverage-badge" title="Coverage artifacts detected">
                      📊 Coverage
                    </span>
                  )}
                </div>
              )}
              {metadata.build?.ciConfigs && metadata.build.ciConfigs.length > 0 && (
                <div className="ci-configs">
                  <span className="label">CI:</span>
                  <span>{metadata.build.ciConfigs.map(c => (c?.file || '').split('/').pop() || c?.file || '—').join(", ")}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Entrypoints */}
        {!metadataLoading && metadata?.entrypoints && metadata.entrypoints.length > 0 && (() => {
          const apiEntrypoints = metadata.entrypoints.filter(ep => ep.type === 'api');
          const cliEntrypoints = metadata.entrypoints.filter(ep => ep.type === 'cli');
          const jobEntrypoints = metadata.entrypoints.filter(ep => ep.type === 'job');
          
          return (
            <div className="sidebar-block">
              <div className="block-header">
                <PlayCircle size={18} />
                <h3>Entrypoints</h3>
              </div>
              <div className="block-content">
                {apiEntrypoints.length > 0 && (
                  <div className="entrypoint-group">
                    <span className="entrypoint-group-label">API Routes:</span>
                    <ul className="entrypoint-list">
                      {apiEntrypoints.slice(0, 5).map((ep, idx) => (
                        <li key={idx} className="entrypoint-item">
                          <span className="entrypoint-file">{ep.file}</span>
                          {ep.symbol && (
                            <span className="entrypoint-symbol">:{ep.symbol}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {cliEntrypoints.length > 0 && (
                  <div className="entrypoint-group">
                    <span className="entrypoint-group-label">CLI:</span>
                    <ul className="entrypoint-list">
                      {cliEntrypoints.slice(0, 3).map((ep, idx) => (
                        <li key={idx} className="entrypoint-item">
                          <span className="entrypoint-file">{ep.file}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {jobEntrypoints.length > 0 && (
                  <div className="entrypoint-group">
                    <span className="entrypoint-group-label">Jobs/Schedulers:</span>
                    <ul className="entrypoint-list">
                      {jobEntrypoints.slice(0, 3).map((ep, idx) => (
                        <li key={idx} className="entrypoint-item">
                          <span className="entrypoint-file">{ep.file}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Key Flows */}
        {flows && flows.length > 0 && (
          <div className="sidebar-block">
            <div className="block-header">
              <GitBranch size={18} />
              <h3>Key Flows</h3>
            </div>
            <div className="block-content">
              <ul className="flow-list">
                       {flows.slice(0, 5).map((flow) => (
                         <li key={flow.flowId} className="flow-item">
                           <div className="flow-link-disabled">
                             <span className="flow-name">{flow.name}</span>
                           </div>
                           <span className="flow-type">{flow.entrypointType}</span>
                         </li>
                       ))}
              </ul>
            </div>
          </div>
        )}

        {/* Data Model */}
        {!metadataLoading && metadata?.database && (
          <div className="sidebar-block">
            <div className="block-header">
              <Database size={18} />
              <h3>Data Model</h3>
            </div>
            <div className="block-content">
              {metadata.database.orm && (
                <div className="database-orm">
                  <span className="label">ORM:</span>
                  <span>
                    {metadata.database.orm}
                    {metadata.database.dbType && (
                      <span className="db-type"> ({metadata.database.dbType})</span>
                    )}
                  </span>
                </div>
              )}
              {metadata.database.migrationDirs && metadata.database.migrationDirs.length > 0 && (
                <div className="migration-dirs">
                  <span className="label">Migrations:</span>
                  <span>{metadata.database.migrationDirs.join(", ")}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CTAs */}
        <div className="sidebar-block cta-block">
          <div className="block-content">
            <button
              className="cta-button primary"
              onClick={() => onOpenChat && onOpenChat("Tell me about the architecture of this repository")}
            >
              <MessageSquare size={18} />
              Ask a question about this repo
            </button>
            {keyFiles.length > 0 && (
              <button
                className="cta-button secondary"
                onClick={() => onOpenFiles && onOpenFiles(keyFiles)}
              >
                <FileText size={18} />
                Open key files ({keyFiles.length})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemOverview;

